import { Injectable } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppDialogUiReduxObject } from '@safarilaw-webapp/shared/dialog/store-access';

import { SafariObjectId } from '@safarilaw-webapp/shared/common-objects-models';
import { FileDownloadService } from '@safarilaw-webapp/shared/crud';
import { v4 as uuidv4 } from 'uuid';
import { dialogControlMixin } from '../../../forms/components/safari-base-page/dialog-control-mixin';

export class FileWithId {
  file: File;
  id: string;
  attachmentFolderId?: SafariObjectId;
}
export class FileWhitelist {
  types: string[];
  typeName: string;
}
//  IMPORTANT! This is a COMPONENT-ONLY service meant to extend any given component with file processing features
//  It must not be used on a module or root level - only on the component level (providers: [FileManagementComponentService] in the component)
@Injectable({
  providedIn: null
})
export class FileManagementComponentService extends dialogControlMixin(class {}) {
  private _files: FileWithId[] = [];
  public get files(): readonly FileWithId[] {
    return this._files;
  }
  public allowUploads = true;
  public maxFileSizeInMb = 1024;
  public maxNumFiles: number = Number.MAX_SAFE_INTEGER;
  constructor(
    private _fileDownloadService: FileDownloadService,
    public _actions: Actions,
    public _store: Store<any>,
    public _appDialogUiReduxObject: AppDialogUiReduxObject
  ) {
    super();
  }

  onFilesAdded(event: { addedFiles: { [key: string]: File } }) {
    if (!this.allowUploads) {
      return;
    }
    this._addFiles(event.addedFiles);
  }

  public isFileSizeValid(file): boolean {
    return !(file == null || file.size === 0 || file.size / 1024 / 1024 > this.maxFileSizeInMb);
  }

  loadFileAsDataUrl(file, callbackLambdaFunc) {
    this._fileDownloadService.loadFileAsDataUrl(
      file,
      null,
      o => {
        callbackLambdaFunc(o);
      },
      null,
      null,
      true
    );
  }
  public removeFileByIndex(index: number) {
    if (index > -1 && index < this._files.length) {
      this._files.splice(index, 1);
    }
  }
  public removeFileById(id: string) {
    const index = this._files.findIndex(o => o.id == id);
    this.removeFileByIndex(index);
  }
  addFile(file: File) {
    this._addFile(file);
  }
  private _addFile(file: File) {
    if (!this.allowUploads) {
      return;
    }
    if (this._files.length === this.maxNumFiles) {
      if (this.maxNumFiles === 1) {
        this._files = [];
      } else {
        return false;
      }
    }
    const id = uuidv4();
    this._files.push({ id, file });
    return true;
  }
  private _addFiles(files: { [key: string]: File }) {
    if (!this.allowUploads) {
      return;
    }
    let arrayIsWithinLimit = true;
    const filesWithSizeError = [];
    for (const key in files) {
      if (!isNaN(parseInt(key, 10))) {
        const file = files[key];
        if (!this.isFileSizeValid(file)) {
          filesWithSizeError.push(file.name);
        }
        if (this._addFile(files[key]) == false) {
          arrayIsWithinLimit = false;
          break;
        }
      }
    }

    this._notifyFileErrors(filesWithSizeError, arrayIsWithinLimit);
  }

  /** Remove attachm ents pending upload */
  clearFiles() {
    this._files = [];
  }

  private _notifyFileErrors(filesWithSizeError, arrayIsWithinLimit) {
    if (filesWithSizeError.length === 0) {
      return;
    }
    let result = 'The following issues occurred while trying to add files <br><br><ul>';
    if (!arrayIsWithinLimit) {
      result += `<li> You can only add up to ${this.maxNumFiles} files at a time. </li><br>`;
    }
    if (filesWithSizeError.length > 0) {
      result += `<li> File size must not be zero or more than ${this.maxFileSizeInMb} MB </li><br>(`;
      for (let i = 0; i < filesWithSizeError.length; i++) {
        result += filesWithSizeError[i];
        if (i < filesWithSizeError.length - 1) {
          result += ', ';
        }
      }

      result += ')';
    }

    result += '</ul>';
    this.showDialog1ButtonOnce$('File Validation Errors Occurred', result).subscribe();
  }
}
